import debounce from 'lodash/debounce';
import { QualityTypes } from '@cloudinary/url-gen/types/types';
import { useCallback, useEffect, useState } from 'react';
import { useCloudinary } from './Cloudinary/useCloudinary';
import { useWindowResize } from './Window/useWindowResize';
import { FocusMethod } from '../../../types/CloudinaryImageType';

type Props = {
  imageId?: string;
  initialDimensions?: { width?: number; height?: number };
  quality?: QualityTypes;
  focus?: FocusMethod;
};

export const useResponsiveImage = ({ imageId, initialDimensions, quality = 'auto:good', focus }: Props) => {
  const { getResponsiveImage } = useCloudinary();

  const imageFunction = useCallback(
    (image: string, dimensions?: { width?: number; height?: number }) =>
      dimensions
        ? getResponsiveImage(image, {
            width: dimensions?.width,
            height: dimensions?.height,
            focusMethod: focus,
            quality,
          })
        : getResponsiveImage(image, { quality }),
    [getResponsiveImage, quality, focus],
  );

  const [mainImage, setMainImage] = useState(imageId ? imageFunction(imageId, initialDimensions) : undefined);

  // Primitives, not the object: callers pass `initialDimensions` inline, so depending on it
  // directly would rebuild this every render.
  const { width: initialWidth, height: initialHeight } = initialDimensions ?? {};

  const setResponsiveImage = useCallback(() => {
    if (imageId && imageFunction) {
      // Pass the dimensions through — `getResponsiveImage` skips `.resize()` without them, so
      // SSR's correctly-sized image was replaced on mount by the full-size original.
      setMainImage(imageFunction(imageId, { width: initialWidth, height: initialHeight }));
    }
  }, [imageId, imageFunction, initialWidth, initialHeight]);

  useEffect(() => {
    setResponsiveImage();
  }, [setResponsiveImage]);

  useWindowResize(debounce(() => setResponsiveImage(), 200));

  return mainImage;
};
